import React, { useEffect } from 'react';
import { Outlet, useLocation, useNavigate } from 'react-router-dom';

// material-ui
import { Theme } from '@mui/material/styles';
import useMediaQuery from '@mui/material/useMediaQuery';
import Box from '@mui/material/Box';
import Container from '@mui/material/Container';
import Toolbar from '@mui/material/Toolbar';

// project import
import Header from './Header';
import Loader from 'components/Loader';
import Breadcrumbs from 'components/@extended/Breadcrumbs';
import AuthGuard from 'utils/route-guard/AuthGuard';
import { useDocumentTitle } from 'hooks/useDocumentTitle';
import KeyboardShortcutsModal from 'components/KeyboardShortcutsModal';
import { useShortcutsModal } from 'hooks/useShortcutsModal';
import { useGlobalShortcuts } from 'hooks/useGlobalShortcuts';

import { MenuOrientation } from 'config';
import useConfig from 'hooks/useConfig';
import { handlerDrawerOpen, useGetMenuMaster } from 'api/menu';

import { appInsights } from "utils/appInsights";

// ==============================|| MAIN LAYOUT ||============================== //

export default function PharoLayout() {
  const { menuMasterLoading } = useGetMenuMaster();
  const navigate = useNavigate();

  // Register the iOS push notification deep-link bridge.
  // Called by the iOS shell via WKWebView.evaluateJavaScript when the user taps
  // a push notification that carries an orderSeqId payload.
  useEffect(() => {
    (window as any).__omsHandlePushDeepLink = (orderSeqId: string) => {
      navigate('/order-edit', { state: { orderSeqId } });
    };
    return () => {
      delete (window as any).__omsHandlePushDeepLink;
    };
  }, [navigate]);
  const matchDownXL = useMediaQuery((theme: Theme) => theme.breakpoints.down('xl'));
  const downLG = useMediaQuery((theme: Theme) => theme.breakpoints.down('lg'));

  const { container, miniDrawer, menuOrientation } = useConfig();

  const isHorizontal = menuOrientation === MenuOrientation.HORIZONTAL && !downLG;

  // Keyboard shortcuts modal
  const { isOpen: isShortcutsModalOpen, openModal: openShortcutsModal, closeModal: closeShortcutsModal, currentPage } = useShortcutsModal();

  // Global shortcuts handler
  useGlobalShortcuts({
    onShowShortcuts: openShortcutsModal,
    onCancel: closeShortcutsModal,
    enableBrowserFallback: true,
  });

  // Set document title based on user role
  useDocumentTitle();

  // set media wise responsive drawer
  useEffect(() => {
    if (!miniDrawer) {
      handlerDrawerOpen(!matchDownXL);
    }
    // eslint-disable-next-line
  }, [matchDownXL]);

  useEffect(() => {
    handlerDrawerOpen(false);
  }, []);

  const location = useLocation();

  useEffect(() => {
    appInsights.trackPageView({ name: location.pathname });
  }, [location]);

  if (menuMasterLoading) return <Loader />;

  return (
    <AuthGuard>
      <>
      <Box sx={{ display: 'flex', width: '100%' }}>
          <Header onOpenShortcutsModal={openShortcutsModal} />
        <Box
          component="main"
            sx={{
            width: '100%',
            flexGrow: 1,
            p: { xs: 2, sm: 3 },
            mt: { xs: 7.5, sm: 8.5 },
            ...(container && {
              ml: { xs: 0, lg: 0 },
              mr: { xs: 0, lg: 0 }
            })
            }}
          >
          <Container maxWidth={false}>
            <Breadcrumbs title titleBottom card={false} divider={false} />
            <Outlet />
          </Container>
        </Box>
      </Box>
        
        {/* Keyboard Shortcuts Modal */}
        <KeyboardShortcutsModal
          open={isShortcutsModalOpen}
          onClose={closeShortcutsModal}
          currentPage={currentPage}
        />
      </>
    </AuthGuard>
  );
}
